import {
  Component,
  ChangeDetectionStrategy,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PrimerComponent } from '../primer.component';
import { IHeadlessPaymentMethodButton, Primer } from '@primer-io/checkout-web';
import { OrderformRedirectResponse } from 'libs/orderform-widgets/src/lib/ngrx/orderform.models';
import { PrimerPaymentMethodType, PrimerPaypalMethodType } from '../primer.model';
import { PrimerConfirmModalComponent } from '../primer-confirm-modal/primer-confirm-modal.component';

@Component({
  selector: 'orderform-payment-method-primer-paypal',
  templateUrl: './primer-paypal.component.html',
  styleUrls: ['./primer-paypal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrimerPaypalComponent),
      multi: true,
    },
  ],
})
export class PrimerPaypalComponent extends PrimerComponent {
  private paypalButton: IHeadlessPaymentMethodButton;
  private paymentMethodManager: any;
  paymentMethodType: PrimerPaymentMethodType = PrimerPaymentMethodType.NONE;
  @Output() readonly paymentMethodTypeDetected = new EventEmitter<PrimerPaypalMethodType>();
  private paypalButtonId = 'paypal-button';
  protected modalInstance: PrimerConfirmModalComponent | null = null;

  async initializePrimerHeadless() {
    try {
      const order = await this.getOrder();
      const clientSession = await this.createClientSession(order);
      this.clientToken = clientSession.clientToken;
      if (!clientSession || !this.clientToken) {
        this._toaster.error('Payment provider is currently unavailable. Please try another payment method.');
        return;
      }

      this._headless = await Primer.createHeadless(this.clientToken, {
        onAvailablePaymentMethodsLoad: async (paymentMethods) => {
          // Only handle PayPal payment method
          for (const paymentMethod of paymentMethods) {
            const { managerType } = paymentMethod;
            if (managerType === 'NATIVE') {
              this.paymentMethodManager = await this._headless.createPaymentMethodManager(
                PrimerPaypalMethodType.PAYPAL
              );

              this.paymentMethodType = PrimerPaymentMethodType.PAYPAL;

              this.paymentMethodTypeDetected.emit(PrimerPaypalMethodType.PAYPAL);
              break;
            } else {
              this.paymentMethodTypeDetected.emit(PrimerPaypalMethodType.NONE);
            }
          }
        },
        onCheckoutComplete: async (data) => {
          await this._handleCheckoutComplete(data);
        },
        onCheckoutFail: async (error, { payment }, handler) => {
          return this._handleCheckoutFail(error, payment, handler, 'ds-orderform-payment-primer-paypal');
        },
        onPaymentMethodAction: (action) => {
          if (action === 'PAYMENT_METHOD_SELECTED') {
            if (this.modalInstance) {
              this.modalInstance.setProcessingOrder(true);
            }
          } else if (action === 'PAYMENT_METHOD_UNSELECTED') {
            if (this.modalInstance) {
              this.modalInstance.setProcessingOrder(false);
            }
          }
        },
      });

      await this._headless.start();
    } catch (error) {
      this._hidePaymentMethodWithError(error, 'primer-paypal');
    }
  }

  async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }

    const refreshed = await this._headless.refreshClientSession();

    if (!refreshed || !this.clientToken) {
      this._paymentDataChanged.next({
        valid: false,
      });
      return { valid: false };
    }

    this._paymentDataChanged.next({
      valid: true,
      primer_token: this.clientToken,
    });
    return { valid: true };
  }

  async _handlePrimerRedirect(redirectData: OrderformRedirectResponse): Promise<void> {
    const shouldContinue = await this._handlePrimerRedirectCommon(redirectData);
    if (!shouldContinue) return;

    this.openPayPalModal();
    this._numberOfSubmitCalls++;
  }

  protected getComponentName(): string {
    return 'ds-orderform-payment-primer-paypal';
  }

  protected async openPayPalModal() {
    await this._headless.refreshClientSession();

    const dialogRef = this._dialog.open(PrimerConfirmModalComponent, {
      data: {
        paymentMethodType: this.paymentMethodType,
        buttonId: this.paypalButtonId,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this._numberOfSubmitCalls = 0;
    });

    this.paypalButton = this.paymentMethodManager.createButton();
    this.modalInstance = dialogRef.componentInstance;
    this.modalInstance.paymentMethodType = this.paymentMethodType;
    this.modalInstance.buttonId = this.paypalButtonId;
    this.modalInstance.dialogRef = dialogRef;
    this.modalInstance.paymentButton = this.paypalButton;
  }
}
